import React, { useCallback, useMemo } from 'react';

import Wahanda from 'common/wahanda';
import { htmlToText } from 'common/dom-helpers';
import { PaymentProtectionBadge } from 'components/shared/PaymentProtectionBadge';
import { Reconnect } from 'src/federation/Reconnect';
import { getItemIcon, getItemStatus } from 'services/bookings-report';
import type { BookingsReportTableRow } from 'services/bookings-report';

export type BookingsReportTableProps = {
  rows: BookingsReportTableRow[];
  onRowClick: (row: BookingsReportTableRow) => void;
  homeDataRef: React.RefObject<HTMLDivElement>;
  isError: boolean;
  isLoading: boolean;
};

const getRowId = (item: BookingsReportTableRow, index: number): string =>
  `${item.type}-${item.id}-${index}`;

const BookingsReportTableBase = ({
  rows,
  onRowClick,
  homeDataRef,
  isError,
  isLoading,
}: BookingsReportTableProps) => {
  const headers = useMemo(
    () => [
      '',
      Wahanda.lang.home.table.headings.orderRef,
      Wahanda.lang.home.table.headings.date,
      Wahanda.lang.home.table.headings.appointmentDatetime,
      Wahanda.lang.home.table.headings.employee,
      Wahanda.lang.home.table.headings.customer,
      Wahanda.lang.home.table.headings.service,
      Wahanda.lang.home.table.headings.price,
      Wahanda.lang.home.table.headings.status,
    ],
    [],
  );

  const tableRows = useMemo(
    () =>
      rows.map((item, index) => {
        const dateInfo = Wahanda.Date.formatToDefaultFullDate(Wahanda.Date.parse(item.date));
        const dateStr = `${dateInfo.date} ${dateInfo.time}`;
        const icon = getItemIcon(item);
        const status = getItemStatus(item);
        return {
          id: getRowId(item, index),
          cells: [
            {
              content: (
                <div className={`icon icons-${icon.className}`} title={icon.title || undefined} />
              ),
            },
            {
              content: htmlToText(item.orderRef),
            },
            dateStr,
            item.appointmentDatetime || 'N/A',
            item.employeeName || 'N/A',
            item.customer,
            item.service,
            item.value ? Wahanda.Currency.format(item.value as never) : '',
            status.badges.length ? (
              <span>
                {status.badges.map((badge, badgeIndex) => (
                  <React.Fragment key={`${item.type}-${item.id}-${badgeIndex}`}>
                    {badge.type === 'paymentProtection' ? (
                      <PaymentProtectionBadge />
                    ) : (
                      <span className={`status status-${badge.className}`}>{badge.text}</span>
                    )}
                    {badgeIndex < status.badges.length - 1 ? <br /> : null}
                  </React.Fragment>
                ))}
              </span>
            ) : (
              ''
            ),
          ],
        };
      }),
    [rows],
  );

  const rowsById = useMemo(() => {
    const map = new Map<string, BookingsReportTableRow>();
    rows.forEach((item, index) => {
      map.set(getRowId(item, index), item);
    });
    return map;
  }, [rows]);

  const handleReconnectRowClick = useCallback(
    (rowIndex: number, rowId: string) => {
      const row = rowsById.get(rowId) ?? rows[rowIndex];
      if (row) {
        onRowClick(row);
      }
    },
    [rows, rowsById, onRowClick],
  );

  const reconnectTableProps = useMemo(
    () => ({
      headers,
      rows: tableRows,
      onRowClick: handleReconnectRowClick,
      isError,
      isLoading,
      emptyTitle: Wahanda.lang.home.table.empty,
      emptyIcon: 'caution',
      testID: 'bookings-report-table',
    }),
    [headers, tableRows, handleReconnectRowClick, isError, isLoading],
  );

  return (
    <div ref={homeDataRef} className="home-data home-data--scroll" data-hj-suppress>
      <Reconnect name="Table" props={reconnectTableProps} />
    </div>
  );
};

export const BookingsReportTable = React.memo(BookingsReportTableBase);
